import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { LeadAdmin, Piano } from '../lib/tipi'

interface RispostaMiei {
  leads: LeadAdmin[]
  totale: number
  limite: number | null   // null = illimitato
  disponibili: number | null
  piano: Piano
}

interface RispostaContatore {
  utilizzati: number
  limite: number | null
  disponibili: number | null
  piano: Piano
}

/**
 * Hook centrale per la gestione dei lead privati dell'utente loggato.
 *
 * - `lista` carica i lead dell'utente + i limiti
 * - `crea/modifica/elimina` sono mutation che invalidano la cache
 * - `puoCreare` tiene conto del piano (Pro: 10, Enterprise: ∞, Free: 0)
 */
export function useMieiLead() {
  const queryClient = useQueryClient()

  const lista = useQuery({
    queryKey: ['miei-lead'],
    queryFn: async () =>
      (await api.get<{ dati: RispostaMiei }>('/leads/miei')).data.dati!,
  })

  const totale = lista.data?.totale ?? 0
  const limite = lista.data?.limite ?? null
  const disponibili = lista.data?.disponibili ?? null
  const piano = lista.data?.piano

  const puoCreare = limite === null || totale < limite

  const invalida = () => {
    queryClient.invalidateQueries({ queryKey: ['miei-lead'] })
    queryClient.invalidateQueries({ queryKey: ['miei-lead', 'contatore'] })
    queryClient.invalidateQueries({ queryKey: ['leads'] })
  }

  const crea = useMutation({
    mutationFn: async (dati: Record<string, unknown>) =>
      (await api.post<{ dati: LeadAdmin }>('/leads', dati)).data.dati!,
    onSuccess: invalida,
  })

  const modifica = useMutation({
    mutationFn: async (vars: { id: string; dati: Record<string, unknown> }) =>
      (await api.put<{ dati: LeadAdmin }>(`/leads/${vars.id}`, vars.dati)).data.dati!,
    onSuccess: invalida,
  })

  const elimina = useMutation({
    mutationFn: async (id: string) => api.delete(`/leads/${id}`),
    onSuccess: invalida,
  })

  return {
    leads: lista.data?.leads ?? [],
    totale,
    limite,
    disponibili,
    piano,
    puoCreare,
    isLoading: lista.isLoading,
    error: lista.error,
    crea,
    modifica,
    elimina,
  }
}

/**
 * Hook leggero per il solo contatore — usato dal badge in sidebar.
 * Non scarica la lista completa.
 */
export function useContatoreMieiLead() {
  return useQuery({
    queryKey: ['miei-lead', 'contatore'],
    queryFn: async () =>
      (await api.get<{ dati: RispostaContatore }>('/leads/miei/contatore')).data.dati!,
    staleTime: 30_000,
  })
}
